/**
 * The runtime store of one mounted tab spec: the draft document, the
 * question catalogue and what the review found in it, the engine session and
 * each table's run. A zustand vanilla store provided through React context,
 * so every mounted spec owns its own. Not persisted.
 */

import { createContext, createElement, ReactNode, useContext } from "react"
import { createStore, StoreApi, useStore } from "zustand"
import type { HierarchicalDataEntry } from "types/engineOutput.types"
import type { TableModel } from "utils/tableModel"
import { compileSpecTable, TablePlan } from "../compiler/table"
import { canonicalJson, fnv1a } from "../model/hash"
import { Finding, reconcile } from "../model/reconcile"
import type { PreparationPart } from "./preparation"
import {
  applyLabelChanges,
  LabelChange,
  LabelTarget,
  variableSlice,
} from "../model/shownLabels"
import { Catalogue, findInstance, findQuestion, ValueRange } from "../types/catalogue"
import {
  Banner,
  Condition,
  schemaVersionFor,
  ShownLabel,
  TableSpec,
  TabSpecDefaults,
  TabSpecDocument,
} from "../types/tabSpecDocument"

export type TableRunStatus =
  | "idle"
  | "queued"
  | "running"
  | "done"
  | "error"
  | "skipped"
/** What a table shows: its run, or why the run no longer counts. */
export type DisplayStatus = TableRunStatus | "stale" | "excluded"

export interface TableRun {
  status: TableRunStatus
  /** `planKey` of the plan that ran; "" while the table waits in a book run. */
  key: string
  model?: TableModel
  /** Why the table was skipped or failed. */
  message?: string
  notes: string[]
  warnings: string[]
  plan?: TablePlan
}

export interface SessionState {
  instance: string | null
  /**
   * "no-data": the project has no data file; the spec can still be edited.
   * "prep-error": the data is open and the codebook loaded, but the
   * preparation failed (`prepError` says where and why); no table runs.
   */
  status: "idle" | "starting" | "ready" | "no-data" | "prep-error" | "error"
  error?: string
  prepError?: string
  /** Bumped by every new session, so runs of an earlier one are stale. */
  generation: number
  /** Banner tables created in this session. */
  knownBanners: Set<string>
  /** The parts of the preparation the session ran, for naming them in a trace. */
  preparation?: PreparationPart[]
}

/** The progress of a run of the whole tab book. */
export interface BookRun {
  total: number
  /** Tables the book has got through, whatever their outcome. */
  done: number
  failed: number
  skipped: number
  cancelled: boolean
  active: boolean
  /** Why the book stopped by itself, for the run dialog: the engine stopped answering. */
  reason?: string
}

export interface TabSpecState {
  projectId: string
  owner: string
  name: string
  /** The project has no questionnaire (set once, by the route). */
  dataOnly: boolean
  document: TabSpecDocument
  /** The document as last saved or loaded. */
  savedDocument: TabSpecDocument
  /** `JSON.stringify` of `savedDocument`. */
  savedSnapshot: string
  loadedEtag: string | null
  codebook: HierarchicalDataEntry[] | null
  /** With the session's values check in it (`valueRanges`) once the check has read a field. */
  catalogue: Catalogue | null
  /**
   * What the values check of the current session found, by field
   * (`runtime/valueCheck.ts`). Every catalogue the store is given carries
   * it; a new session starts it empty.
   */
  valueRanges: Record<string, ValueRange>
  routingMessage: string | null
  findings: Finding[]
  session: SessionState
  runs: Record<string, TableRun>
  bookRun: BookRun | null
  selectedTableId: string | null
  /**
   * The tables the assistant added or changed since the spec was last saved
   * (each shows an "AI" tag). `markSaved` clears them.
   */
  aiTableIds: string[]

  // document
  updateTable: (id: string, patch: Partial<TableSpec>) => void
  /** Removes override fields from a table, so the default applies again. */
  clearOverrides: (id: string, fields: (keyof TableSpec)[]) => void
  moveTable: (id: string, toIndex: number) => void
  addTables: (tables: TableSpec[]) => void
  removeTable: (id: string) => void
  setIncluded: (ids: string[], include: boolean) => void
  updateDefaults: (patch: Partial<TabSpecDefaults>) => void
  upsertBanner: (banner: Banner) => void
  /** Refused (returns false) for the default banner and for the last banner. */
  removeBanner: (id: string) => boolean
  updateData: (patch: Partial<TabSpecDocument["data"]>) => void
  updateVariant: (variant: string | null) => void
  /** Sets the book filter; one without a group removes it, so the document has no `filter` key. */
  setBookFilter: (filter: Condition) => void
  /** Applies shown-label changes in one document write; nothing when they change nothing. */
  setShownLabels: (changes: LabelChange[]) => void
  markSaved: (document: TabSpecDocument, etag: string) => void
  /**
   * Puts the assistant's proposed document in place of the draft, as an
   * edit: the saved document stays, so the spec is dirty until it is saved.
   * `aiTableIds` (the tables it added or changed) join the tagged ones that
   * are still in the document.
   */
  loadDraft: (document: TabSpecDocument, aiTableIds: string[]) => void
  /**
   * Puts an earlier draft back (the Undo of a proposal), as an edit. The
   * tagged tables become `aiTableIds` when given, else those of the tagged
   * ones that are still in the document.
   */
  revertTo: (document: TabSpecDocument, aiTableIds?: string[]) => void

  // sources
  setCodebook: (codebook: HierarchicalDataEntry[] | null) => void
  setCatalogue: (catalogue: Catalogue | null, routingMessage: string | null) => void
  /** Adds what the values check of session `generation` found; nothing once another session has begun. */
  setValueRanges: (generation: number, found: Record<string, ValueRange>) => void

  // runtime
  setSession: (patch: Partial<SessionState>) => void
  /** A new session: generation + 1, no known banners, every run cleared. */
  beginSession: (instance: string | null) => void
  addKnownBanner: (name: string) => void
  /** Takes a banner out of the known set again: its build failed or was never sent. */
  forgetKnownBanner: (name: string) => void
  setRun: (id: string, run: TableRun) => void
  setBookRun: (run: BookRun | null) => void
  cancelBook: () => void
  select: (id: string | null) => void
}

/** The statuses of a run that has ended. */
export const FINISHED: TableRunStatus[] = ["done", "error", "skipped"]

/** Overrides that go away when cleared. */
const DELETABLE = [
  "title",
  "bannerId",
  "base",
  "baseText",
  "significance",
  "statistics",
  "meanStats",
  "values",
  "note",
  "summary",
  "slice",
  "hiddenRows",
  "sort",
  "hideEmpty",
] as const
type DeletableField = (typeof DELETABLE)[number]
/** Overrides that go back to null (the defaults) when cleared. */
const NULLABLE = ["rows", "scores"] as const
type NullableField = (typeof NULLABLE)[number]

const isDeletable = (f: keyof TableSpec): f is DeletableField =>
  (DELETABLE as readonly string[]).includes(f)
const isNullable = (f: keyof TableSpec): f is NullableField =>
  (NULLABLE as readonly string[]).includes(f)

const findingsOf = (
  document: TabSpecDocument,
  catalogue: Catalogue | null
): Finding[] => (catalogue ? reconcile(document, catalogue) : [])

/** `catalogue` with the values check of the session in it; as it is while the check has read nothing. */
const withValues = (
  catalogue: Catalogue | null,
  valueRanges: Record<string, ValueRange>
): Catalogue | null =>
  catalogue && Object.keys(valueRanges).length > 0 ? { ...catalogue, valueRanges } : catalogue

/** `catalogue` without the values check of an earlier session. */
const withoutValues = (catalogue: Catalogue | null): Catalogue | null => {
  if (!catalogue || catalogue.valueRanges === undefined) return catalogue
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { valueRanges: _earlier, ...rest } = catalogue
  return rest
}

/**
 * `JSON.stringify` of each document, made once per document object: the
 * store replaces a document on every edit and never changes one in place.
 */
const serialised = new WeakMap<TabSpecDocument, string>()
const serialise = (document: TabSpecDocument): string => {
  const known = serialised.get(document)
  if (known !== undefined) return known
  const text = JSON.stringify(document)
  serialised.set(document, text)
  return text
}

/**
 * `document` with `labels` (none when undefined) right after `tables`, where
 * `normalizeTabSpecDocument` puts them, and the version its contents then
 * call for: a stored document and an edited one list their keys in one
 * order, so a label set and reset serialise as before.
 */
const withLabels = (
  document: TabSpecDocument,
  labels: ShownLabel[] | undefined
): TabSpecDocument => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { labels: _labels, approval, updatedAt, updatedBy, ...before } = document
  return {
    ...before,
    schemaVersion: schemaVersionFor({ ...before, labels }),
    ...(labels ? { labels } : {}),
    approval,
    updatedAt,
    updatedBy,
  }
}

/**
 * `document` with the version its contents call for (`schemaVersionFor`):
 * 3 once an edit uses a field of version 3, back to 2 or 1 when no field of
 * it is left. The same object when its version is already right.
 */
const versioned = (document: TabSpecDocument): TabSpecDocument => {
  const schemaVersion = schemaVersionFor(document)
  return schemaVersion === document.schemaVersion ? document : { ...document, schemaVersion }
}

/**
 * The raw label a shown label replaces: the option's, the instance's own
 * label (not its id) when the catalogue has it, or the variable's.
 */
const rawLabel = (catalogue: Catalogue | null, t: LabelTarget): string => {
  if (t.option !== undefined) return t.option
  if (t.instance === undefined) return t.variable
  const q = catalogue
    ? (findQuestion(catalogue, t.variable) ?? findQuestion(catalogue, t.variable, "data"))
    : null
  return (q ? findInstance(q, t.instance)?.label : undefined) ?? t.instance
}

export const createTabSpecStore = (init: {
  projectId: string
  owner: string
  name: string
  document: TabSpecDocument
  etag: string | null
  /** The project has no questionnaire. */
  dataOnly?: boolean
}): StoreApi<TabSpecState> =>
  createStore<TabSpecState>((set, get) => {
    /**
     * Every document edit goes through here, so the findings and the version
     * follow it. `alongside` is more state written in the same update, from
     * the edited document.
     */
    const setDocument = (
      edited: TabSpecDocument,
      alongside?: (document: TabSpecDocument, s: TabSpecState) => Partial<TabSpecState>
    ): void => {
      const document = versioned(edited)
      set((s) => ({
        document,
        findings: findingsOf(document, s.catalogue),
        ...alongside?.(document, s),
      }))
    }
    /**
     * Replaces the whole draft (an assistant proposal or its Undo) as an
     * edit, with the tagged tables `tagged` that it has. A selected table it
     * does not have is let go.
     */
    const replaceDocument = (replacement: TabSpecDocument, tagged: string[]): void =>
      setDocument(replacement, (document, s) => {
        const ids = new Set(document.tables.map((t) => t.id))
        return {
          aiTableIds: [...new Set(tagged)].filter((id) => ids.has(id)),
          selectedTableId:
            s.selectedTableId !== null && ids.has(s.selectedTableId) ? s.selectedTableId : null,
        }
      })
    const editTables = (fn: (tables: TableSpec[]) => TableSpec[]): void => {
      const doc = get().document
      setDocument({ ...doc, tables: fn(doc.tables) })
    }

    return {
      projectId: init.projectId,
      owner: init.owner,
      name: init.name,
      dataOnly: init.dataOnly === true,
      document: init.document,
      savedDocument: init.document,
      savedSnapshot: serialise(init.document),
      loadedEtag: init.etag,
      codebook: null,
      catalogue: null,
      valueRanges: {},
      routingMessage: null,
      findings: [],
      session: {
        instance: null,
        status: "idle",
        generation: 0,
        knownBanners: new Set(),
      },
      runs: {},
      bookRun: null,
      selectedTableId: null,
      aiTableIds: [],

      updateTable: (id, patch) => {
        // A table's identity, question and origin never change.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: _id, question: _question, origin: _origin, source: _source, ...allowed } = patch
        editTables((tables) =>
          tables.map((t) => (t.id === id ? { ...t, ...allowed } : t))
        )
      },
      clearOverrides: (id, fields) =>
        editTables((tables) =>
          tables.map((t) => {
            if (t.id !== id) return t
            const next: TableSpec = { ...t }
            fields.forEach((f) => {
              if (isNullable(f)) next[f] = null
              else if (isDeletable(f)) delete next[f]
            })
            return next
          })
        ),
      moveTable: (id, toIndex) =>
        editTables((tables) => {
          const from = tables.findIndex((t) => t.id === id)
          if (from < 0) return tables
          const next = [...tables]
          const [moved] = next.splice(from, 1)
          next.splice(Math.max(0, Math.min(toIndex, next.length)), 0, moved)
          return next
        }),
      addTables: (added) => editTables((tables) => [...tables, ...added]),
      removeTable: (id) =>
        editTables((tables) => tables.filter((t) => t.id !== id)),
      setIncluded: (ids, include) => {
        const chosen = new Set(ids)
        editTables((tables) =>
          tables.map((t) => (chosen.has(t.id) ? { ...t, include } : t))
        )
      },
      updateDefaults: (patch) => {
        const doc = get().document
        setDocument({ ...doc, defaults: { ...doc.defaults, ...patch } })
      },
      upsertBanner: (banner) => {
        const doc = get().document
        const exists = doc.banners.some((b) => b.id === banner.id)
        setDocument({
          ...doc,
          banners: exists
            ? doc.banners.map((b) => (b.id === banner.id ? banner : b))
            : [...doc.banners, banner],
        })
      },
      removeBanner: (id) => {
        const doc = get().document
        if (
          id === doc.defaults.bannerId ||
          doc.banners.length <= 1 ||
          !doc.banners.some((b) => b.id === id)
        )
          return false
        setDocument({
          ...doc,
          banners: doc.banners.filter((b) => b.id !== id),
          // Tables that used it fall back to the default banner.
          tables: doc.tables.map((t) => {
            if (t.bannerId !== id) return t
            const next: TableSpec = { ...t }
            delete next.bannerId
            return next
          }),
        })
        return true
      },
      updateData: (patch) => {
        const doc = get().document
        setDocument({ ...doc, data: { ...doc.data, ...patch } })
      },
      updateVariant: (variant) => setDocument({ ...get().document, variant }),
      setBookFilter: (filter) => {
        // Right after the tables, where `normalizeTabSpecDocument` puts it: a
        // book filter set and removed again leaves the document as it was.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { filter: _filter, labels, approval, updatedAt, updatedBy, ...before } =
          get().document
        setDocument({
          ...before,
          ...(filter.length > 0 ? { filter } : {}),
          ...(labels ? { labels } : {}),
          approval,
          updatedAt,
          updatedBy,
        })
      },
      setShownLabels: (changes) => {
        const { document: doc, catalogue } = get()
        const raw = (t: LabelTarget): string => rawLabel(catalogue, t)
        const labels = applyLabelChanges(doc.labels, changes, raw)
        // Compared in canonical order: a stored list in another order that
        // the changes leave as it was is not a change.
        const now = applyLabelChanges(doc.labels, [], raw)
        if (canonicalJson(labels ?? null) === canonicalJson(now ?? null)) return
        setDocument(withLabels(doc, labels))
      },
      markSaved: (saved, etag) => {
        // The saved document carries the stamps of the save. An edit made
        // while the save was under way is kept, and the spec stays dirty.
        const current = get().document
        const stamped: TabSpecDocument = {
          ...current,
          updatedAt: saved.updatedAt,
          updatedBy: saved.updatedBy,
        }
        set({
          savedDocument: saved,
          savedSnapshot: serialise(saved),
          loadedEtag: etag,
          aiTableIds: [],
        })
        setDocument(
          canonicalJson(stamped) === canonicalJson(saved) ? saved : stamped
        )
      },
      loadDraft: (document, aiTableIds) =>
        replaceDocument(document, [...get().aiTableIds, ...aiTableIds]),
      revertTo: (document, aiTableIds) =>
        replaceDocument(document, aiTableIds ?? get().aiTableIds),

      setCodebook: (codebook) => set({ codebook }),
      setCatalogue: (catalogue, routingMessage) =>
        set((s) => ({
          catalogue: withValues(catalogue, s.valueRanges),
          routingMessage,
          findings: findingsOf(s.document, catalogue),
        })),
      setValueRanges: (generation, found) =>
        set((s) => {
          if (s.session.generation !== generation) return {}
          const valueRanges = { ...s.valueRanges, ...found }
          return { valueRanges, catalogue: withValues(s.catalogue, valueRanges) }
        }),

      setSession: (patch) =>
        set((s) => ({ session: { ...s.session, ...patch } })),
      beginSession: (instance) =>
        set((s) => ({
          session: {
            ...s.session,
            instance,
            generation: s.session.generation + 1,
            knownBanners: new Set(),
          },
          runs: {},
          // The values check read the data of the session before: it is read again.
          valueRanges: {},
          catalogue: withoutValues(s.catalogue),
        })),
      addKnownBanner: (name) =>
        set((s) => ({
          session: {
            ...s.session,
            knownBanners: new Set([...s.session.knownBanners, name]),
          },
        })),
      forgetKnownBanner: (name) =>
        set((s) => {
          const knownBanners = new Set(s.session.knownBanners)
          knownBanners.delete(name)
          return { session: { ...s.session, knownBanners } }
        }),
      setRun: (id, run) => set((s) => ({ runs: { ...s.runs, [id]: run } })),
      setBookRun: (bookRun) => set({ bookRun }),
      cancelBook: () =>
        set((s) =>
          s.bookRun ? { bookRun: { ...s.bookRun, cancelled: true } } : {}
        ),
      select: (selectedTableId) => set({ selectedTableId }),
    }
  })

/**
 * The key of what a run of `plan` produces in session `generation`: a run
 * whose key differs from the current plan's is stale. The whole plan counts,
 * not only the script: the title, the row and column labels and the
 * difference rows change the finished table too. Running such a table again
 * sends nothing, since the queue keeps each script's result for the session.
 */
export const planKey = (plan: TablePlan, generation: number): string =>
  fnv1a(`${generation}|${canonicalJson(plan)}`)

/**
 * The tables of each tables array by id (the first table of an id, as
 * `compileSpecTable` finds it). Built once per array.
 */
const tableIndexes = new WeakMap<TableSpec[], Map<string, TableSpec>>()
const tableById = (document: TabSpecDocument, id: string): TableSpec | null => {
  let index = tableIndexes.get(document.tables)
  if (!index) {
    index = new Map()
    for (const t of document.tables) if (!index.has(t.id)) index.set(t.id, t)
    tableIndexes.set(document.tables, index)
  }
  return index.get(id) ?? null
}

/**
 * A table's plan with what it was compiled from. Besides the table itself,
 * `compileSpecTable` reads the defaults, the banners, the data settings (the
 * weight), the book filter, the catalogue and the shown labels of the
 * variables it names, and nothing else of the document: not the other
 * tables, not the variant (the catalogue is built from it), not whether the
 * table is in the tab book.
 */
interface PlanEntry {
  defaults: TabSpecDefaults
  banners: Banner[]
  data: TabSpecDocument["data"]
  filter: Condition | undefined
  catalogue: Catalogue
  /** The shown labels the plan was compiled with, or adopted since (see `sameNames`). */
  labels: ShownLabel[] | undefined
  /**
   * For each variable whose names the plan read (its own, the banner's, the
   * filters', the routing condition's, the weight), `variableSlice` of
   * `labels`: the plan shows no other variable's names.
   */
  slices: Map<string, string>
  plan: TablePlan
  /** The key of `plan` in session `generation`; null until it is asked for. */
  generation: number
  key: string | null
}

/**
 * Whether the plan of `entry` reads the same names under `labels`: every
 * variable it read has the same entries. The entry then takes `labels` on,
 * so the next check is one comparison.
 */
const sameNames = (entry: PlanEntry, labels: ShownLabel[] | undefined): boolean => {
  if (entry.labels === labels) return true
  for (const [variable, slice] of entry.slices)
    if (variableSlice(labels, variable) !== slice) return false
  entry.labels = labels
  return true
}

/**
 * The plan of each table object, kept with its inputs. Every store action
 * replaces the objects it changes and never changes one in place, so a plan
 * is only used for the inputs it was compiled from: an edit of one table
 * compiles that table alone, a change of the defaults, the banners, the data
 * settings, the book filter or the catalogue compiles each table again as it
 * is asked for, a change of the shown labels compiles again the tables that
 * show a variable whose labels changed, and a change of the runs compiles
 * nothing.
 */
const plans = new WeakMap<TableSpec, PlanEntry>()

const planEntry = (
  table: TableSpec,
  document: TabSpecDocument,
  catalogue: Catalogue
): PlanEntry | null => {
  const known = plans.get(table)
  if (
    known &&
    known.defaults === document.defaults &&
    known.banners === document.banners &&
    known.data === document.data &&
    known.filter === document.filter &&
    known.catalogue === catalogue &&
    sameNames(known, document.labels)
  )
    return known
  const seen = new Set<string>()
  const plan = compileSpecTable(table.id, document, catalogue, seen)
  if (!plan) return null
  const entry: PlanEntry = {
    defaults: document.defaults,
    banners: document.banners,
    data: document.data,
    filter: document.filter,
    catalogue,
    labels: document.labels,
    slices: new Map([...seen].map((v) => [v, variableSlice(document.labels, v)])),
    plan,
    generation: 0,
    key: null,
  }
  plans.set(table, entry)
  return entry
}

/**
 * The table's current plan, as `compileSpecTable` gives it; null without a
 * catalogue or for a table the document does not have. Kept per table (see
 * `plans`), so the screens and the runs share one compilation, made again
 * only when the table, the defaults, the banners, the data settings, the
 * book filter, the catalogue or the shown labels of a variable the plan
 * names change.
 */
export const selectPlan = (
  s: Pick<TabSpecState, "document" | "catalogue">,
  tableId: string
): TablePlan | null => {
  const { document, catalogue } = s
  if (!catalogue) return null
  const table = tableById(document, tableId)
  return table ? (planEntry(table, document, catalogue)?.plan ?? null) : null
}

/**
 * The key of the table's current plan in the current session; null without
 * a catalogue or for a table the document does not have. Kept per table
 * (see `plans`), so each table is compiled once per change of the table,
 * the defaults, the banners, the data settings, the book filter, the
 * catalogue or the shown labels of a variable its plan names, and its key
 * worked out once per plan and session generation. The key hashes the whole
 * plan, what it shows included, so a finished run never reads "done" under
 * names it does not show.
 */
export const selectPlanKey = (
  s: Pick<TabSpecState, "document" | "catalogue"> & {
    session: Pick<SessionState, "generation">
  },
  tableId: string
): string | null => {
  const { document, catalogue } = s
  if (!catalogue) return null
  const table = tableById(document, tableId)
  const entry = table ? planEntry(table, document, catalogue) : null
  if (!entry) return null
  const generation = s.session.generation
  if (entry.key === null || entry.generation !== generation) {
    entry.generation = generation
    entry.key = planKey(entry.plan, generation)
  }
  return entry.key
}

/**
 * Whether the document differs from the one last saved or loaded. The
 * document is serialised once per document object, and not at all while it
 * is the saved one.
 */
export const selectIsDirty = (s: TabSpecState): boolean =>
  s.document !== s.savedDocument && serialise(s.document) !== s.savedSnapshot
export const selectTable = (
  s: Pick<TabSpecState, "document">,
  id: string
): TableSpec | null => tableById(s.document, id)
/**
 * The status of the table's run whether or not the table is in the tab book
 * (the plan does not depend on it): a finished run (done, error or skipped)
 * of a plan that has changed since, or of an earlier session, is "stale".
 * Nothing has run without a catalogue. The preview shows this.
 */
export const selectRunStatus = (
  s: TabSpecState,
  tableId: string
): DisplayStatus => {
  const run = s.runs[tableId]
  if (!selectTable(s, tableId) || !s.catalogue || !run) return "idle"
  if (!FINISHED.includes(run.status)) return run.status
  const key = selectPlanKey(s, tableId)
  return key !== null && key === run.key ? run.status : "stale"
}
/**
 * The run's status (`selectRunStatus`), except that a table left out of the
 * tab book is "excluded".
 */
export const selectDisplayStatus = (
  s: TabSpecState,
  tableId: string
): DisplayStatus => {
  const table = selectTable(s, tableId)
  if (table && !table.include) return "excluded"
  return selectRunStatus(s, tableId)
}

export const TabSpecStoreContext =
  createContext<StoreApi<TabSpecState> | null>(null)
export const TabSpecStoreProvider = ({
  store,
  children,
}: {
  store: StoreApi<TabSpecState>
  children: ReactNode
}) => createElement(TabSpecStoreContext.Provider, { value: store }, children)
export const useTabSpecStoreApi = (): StoreApi<TabSpecState> => {
  const store = useContext(TabSpecStoreContext)
  if (!store)
    throw new Error("useTabSpecStore must be used inside TabSpecStoreProvider")
  return store
}
export const useTabSpecStore = <T>(selector: (s: TabSpecState) => T): T =>
  useStore(useTabSpecStoreApi(), selector)
